"use client";

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import Link from 'next/link';
import './operate.css';

/* Saari styling ab operate.css mein hai.
   Scroll animations ki CSS bhi operate.css ke end mein hai (SCROLL ANIMATIONS section).
   Hero background image ka path bhi CSS mein hai:
   operate.css -> ".hero-bg-image" (--hero-bg-mobile / --hero-bg-desktop)

   Images (public/assets/images/ mein daalo):
   - operate-orane-way.png     -> Beyond Ticket Management (Orane Way card)
   - operate-learn.png         -> Learn From Every Issue (quote card)
   - operate-intelligence.png  -> Intelligence for Operations (matrix card)

   Links: "Run the Enterprise" (coverage cards) aur "Operate Across the Enterprise" (eco cards)
   ke `href` field me badlo. */

/* ===================== SCROLL ANIMATION ===================== */
/* Sirf fade-up (neeche se upar), zoom aur fade. Left/right slide nahi.
   'fade' = sirf opacity (jin elements par CSS mein pehle se transform laga hai, jaise loop-core). */
const animGroups: [string, 'fade-up' | 'zoom' | 'fade'][] = [
  ['.sec-head, .opview-head, .why52-head', 'fade-up'],
  ['.coverage-card', 'zoom'],
  ['.monitor-bar', 'fade-up'],
  ['.loop-card', 'zoom'],
  ['.loop-core', 'fade'],
  ['.learn-banner', 'fade-up'],
  ['.ams-card', 'fade-up'],
  ['.op-img-card', 'zoom'],
  ['.cockpit', 'fade-up'],
  ['.stat-card', 'zoom'],
  ['.cockpit-foot', 'fade-up'],
  ['.pipeline', 'fade-up'],
  ['.pipeline-row', 'fade-up'],
  ['.learn-image', 'zoom'],
  ['.intel-item', 'zoom'],
  ['.risk-card', 'fade-up'],
  ['.eco-card', 'zoom'],
  ['.metric-card', 'zoom'],
  ['.phase', 'zoom'],
  ['.journey-caption', 'fade-up'],
  ['.final-cta-box', 'zoom'],
  ['.why52-card', 'zoom'],
  ['.why52-bar', 'fade-up'],
];

function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    animGroups.forEach(([selector, type]) => {
      const perParent = new Map<Element, number>();
      el.querySelectorAll<HTMLElement>(selector).forEach((node) => {
        const parent = node.parentElement as Element;
        const i = perParent.get(parent) ?? 0;
        perParent.set(parent, i + 1);

        node.classList.add('rv', `rv--${type}`);
        node.style.setProperty('--rv-delay', `${(i % 6) * 80}ms`); // stagger
        io.observe(node);
      });
    });

    return () => io.disconnect();
  }, [root]);
}

export default function Operate() {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="operate-page" ref={pageRef}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="hero" data-purpose="hero-section">
          <div className="hero-bg-image"></div>
          <div className="hero-bg-overlay"></div>
          <div className="hero-grid"></div>
          <div className="hero-glow hero-glow--left"></div>
          <div className="hero-glow hero-glow--right"></div>

          <div className="op-container op-container--left hero-inner">
            <div className="hero-layout">
              <div className="hero-content">
                <h1 className="hero-title">Operate &amp; Improve</h1>

                <p className="hero-subtitle">Keep the enterprise running. Make it better every day.</p>

                <p className="hero-text">
                  From day-to-day operations to continuous improvement, Orane helps you run your
                  enterprise reliably, resolve issues faster, prevent recurring problems and
                  unlock more value from your digital and ERP investments.
                </p>

                <div className="hero-actions">
                  <Link className="btn-primary" href="/tellusyourproblem#tell-details">
                    Talk to Orane
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      ></path>
                    </svg>
                  </Link>
                  <button className="btn-glass" type="button">
                    See How It Works
                    <span className="play-icon">▶</span>
                  </button>
                </div>

                <div className="hero-stats"></div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================= RUN THE ENTERPRISE ====================== */}
        <section className="op-section op-section--slate" data-purpose="run-the-enterprise">
          <div className="op-container op-container--left">
            <div className="sec-head">
              <div className="sec-badge">Coverage Matrix</div>
              <h2 className="sec-title">Run the Enterprise</h2>
              <p className="sec-sub">
                End-to-end application and infrastructure operations across your enterprise landscape.
              </p>
            </div>

            <div className="coverage-grid">
              {[
                {
                  title: 'SAP S/4HANA',
                  href: '/enterprise-transformation/sap-s4hana',
                  tag: 'Core ERP Operations',
                  desc: '24/7 business-critical continuity, functional enhancements, patch governance, and transaction performance tuning.',
                  icon: (
                    <path
                      d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7zM9 9h6v6H9z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'SuccessFactors',
                  href: '/enterprise-transformation/successfactors',
                  tag: 'HXM Lifecycle',
                  desc: 'Employee central lifecycle administration, payroll integration resilience, role-based governance, and release upgrades.',
                  icon: (
                    <path
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'BTP & Integrations',
                  href: '/enterprise-transformation/sap-btp-integration',
                  tag: 'Middleware Telemetry',
                  desc: 'Integration Suite, API Management, cloud microservices, queue diagnostics, and third-party SaaS bridge supervision.',
                  icon: (
                    <path
                      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'Enterprise Apps',
                  href: '/enterprise-transformation/digital-engineering',
                  tag: 'Custom Portals & Legacy',
                  desc: 'Supply chain portals, bespoke customer solutions, MES production interfaces, and enterprise middleware operations.',
                  icon: (
                    <path
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'Cloud Operations',
                  href: '/enterprise-transformation/cloud',
                  tag: 'AWS / Azure / Multi-Cloud',
                  desc: 'FinOps cost governance, automated provisioning, elastic backup recovery drills, and continuous high-availability design.',
                  icon: (
                    <path
                      d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 00-9.78 2.096A4.001 4.001 0 003 15z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'ITSM & ITIL v4',
                  href: '/Operate/itsm',
                  tag: 'Incidents, Requests, Changes',
                  desc: 'Smart ticket triaging, automated CAB orchestration, service catalogue governance, and guaranteed response SLAs.',
                  icon: (
                    <path
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'Enhancements & Releases',
                  href: '/Operate/continuous-improvement',
                  tag: 'Agile Sprints & Deployments',
                  desc: 'Bi-weekly micro-enhancement cycles, continuous regression testing, transport management, and user training collateral.',
                  icon: (
                    <path
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
                {
                  title: 'Security & Controls',
                  href: '/Operate/cyber',
                  tag: 'Audit, SOX & GRC',
                  desc: 'Segregation of duties (SoD), role auditing, quarterly compliance certifications, patch cadence, and vulnerability scans.',
                  icon: (
                    <path
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  ),
                },
              ].map((card) => {
                const body = (
                  <>
                    <div>
                      <div className="coverage-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {card.icon}
                        </svg>
                      </div>
                      <h3 className="coverage-title">{card.title}</h3>
                      <p className="coverage-tag">{card.tag}</p>
                      <p className="coverage-desc">{card.desc}</p>
                    </div>
                    <div className="coverage-foot">
                      <span>Operations Managed</span>
                      <span className="coverage-dot"></span>
                    </div>
                  </>
                );
                /* href khaali ho to card plain div rahega (click nahi hoga) */
                return card.href ? (
                  <Link className="coverage-card" key={card.title} href={card.href}>
                    {body}
                  </Link>
                ) : (
                  <div className="coverage-card" key={card.title}>
                    {body}
                  </div>
                );
              })}
            </div>

            <div className="monitor-bar">
              <div className="monitor-left">
                <span className="monitor-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  </svg>
                </span>
                <div>
                  <p className="monitor-title">Active Real-Time Monitoring Footprint</p>
                  <p className="monitor-sub">
                    Managing 320+ enterprise production instances with automated heartbeat telemetry.
                  </p>
                </div>
              </div>
              <div className="monitor-right">
                <div className="monitor-stat">
                  <span className="monitor-value monitor-value--blue">99.98%</span>
                  <span className="monitor-label">Core Uptime SLA</span>
                </div>
                <div className="monitor-divider"></div>
                <div className="monitor-stat">
                  <span className="monitor-value monitor-value--navy">&lt; 15 mins</span>
                  <span className="monitor-label">P1 Response Time</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== OPERATE. CONTROL. IMPROVE. ================== */}
        <section className="op-section op-section--white op-section--clip" data-purpose="operate-control-improve">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <h2 className="sec-title">Operate. Control. Improve.</h2>
              <p className="sec-sub sec-sub--base">
                A cohesive continuous delivery lifecycle designed to convert operational noise into
                enterprise asset intelligence.
              </p>
            </div>

            <div className="loop-wrap">
              <div className="loop-grid">
                <div className="loop-card">
                  <div className="loop-num">01</div>
                  <span className="loop-pill">SUPPORT</span>
                  <h3 className="loop-title">Resolve Issues Faster</h3>
                  <p className="loop-text">
                    24/7 dedicated support engineering team. SLA guaranteed resolution, proactive user
                    guidance, and immediate incident stabilization.
                  </p>
                  <div className="loop-foot">Avg Response: 8.5 Minutes</div>
                </div>

                <div className="loop-core">
                  <div className="loop-core-circle pulse-subtle">
                    <span className="loop-core-small">CORE</span>
                    <span className="loop-core-big">OPERATE</span>
                  </div>
                  <div className="loop-core-pill">CONTINUOUS VALUE</div>
                  <h3 className="loop-core-title">Keep Systems Running</h3>
                  <p className="loop-core-text">
                    Zero-downtime architecture, preemptive capacity planning, data pipeline hygiene, and
                    enterprise application reliability.
                  </p>
                  <div className="loop-core-foot">
                    <span className="loop-core-dot"></span>
                    99.98% High Availability
                  </div>
                </div>

                <div className="loop-card">
                  <div className="loop-num">03</div>
                  <span className="loop-pill">IMPROVE</span>
                  <h3 className="loop-title">Find Opportunities &amp; Drive Value</h3>
                  <p className="loop-text">
                    Sprint-based business enhancements, process bottlenecks removal, automated workflows,
                    and high-ROI feature rollouts.
                  </p>
                  <div className="loop-foot">350+ Deliverables / Year</div>
                </div>
              </div>

              <div className="learn-banner">
                <div className="learn-banner-left">
                  <div className="learn-banner-badge">LEARN</div>
                  <div>
                    <h4 className="learn-banner-title">Turn Every Issue into Enterprise Intelligence</h4>
                    <p className="learn-banner-text">
                      Every closed ticket feeds into root-cause pattern analysis, standard operating
                      automated runbooks, and reusable user-facing solutions.
                    </p>
                  </div>
                </div>
                <div className="learn-banner-right">
                  <span className="learn-banner-chip">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path
                        clipRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        fillRule="evenodd"
                      ></path>
                    </svg>
                    Autonomous Root Cause Engine
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== BEYOND TICKET MANAGEMENT ==================== */}
        <section className="op-section op-section--gray" data-purpose="beyond-ticket-management">
          <div className="op-container">
            <div className="sec-head sec-head--center sec-head--mb14">
              <div className="sec-badge">Mindset Shift</div>
              <h2 className="sec-title">Beyond Ticket Management</h2>
              <p className="sec-sub">We don&apos;t just close tickets. We solve business issues.</p>
            </div>

            <div className="compare-grid">
              {/* Traditional AMS */}
              <div className="ams-card">
                <div>
                  <div className="card-head">
                    <div>
                      <h3 className="ams-title">Traditional AMS</h3>
                      <p className="ams-sub">Commodity reactive vendor model</p>
                    </div>
                    <span className="ams-badge">Reactive</span>
                  </div>

                  <div className="ams-steps">
                    {['Ticket Logged in Queue', 'Resolve (Surface Level)', 'Close Ticket & Reset Clock'].map(
                      (step, i) => (
                        <div key={step}>
                          <div className="ams-step">
                            <span className="ams-step-num">{i + 1}</span>
                            {step}
                          </div>
                          {i < 2 && <div className="ams-connector"></div>}
                        </div>
                      )
                    )}
                  </div>

                  <div className="ams-problems">
                    {[
                      'Recurring problems never eliminated',
                      'No visibility into business impact or revenue risk',
                      'Incentivized by ticket count, not resolution permanence',
                    ].map((item) => (
                      <div className="ams-problem" key={item}>
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path
                            clipRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            fillRule="evenodd"
                          ></path>
                        </svg>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ams-result">Result: Endless firefighting &amp; user frustration</div>
              </div>

              {/* Orane Way — image */}
              <div className="op-img-card">
                <img
                  src="/assets/images/operate1.jpg"
                  alt="Orane Operate & Improve – Value-Engineered Operations Model"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ======================= ONE OPERATING VIEW ======================== */}
        <section className="op-section op-section--white" data-purpose="one-operating-view">
          <div className="op-container">
            <div className="opview-head">
              <div>
                <div className="sec-badge">Unified Cockpit</div>
                <h2 className="sec-title">One Operating View</h2>
                <p className="sec-sub sec-sub--base">
                  Complete visibility across your operations. Single pane of glass for IT and business
                  stakeholders.
                </p>
              </div>
              <div className="opview-live">
                <span className="opview-live-dot"></span>
                Real-time Telemetry Active
              </div>
            </div>

            <div className="cockpit">
              <div className="cockpit-bar">
                <div className="cockpit-bar-left">
                  <div className="cockpit-light cockpit-light--red"></div>
                  <div className="cockpit-light cockpit-light--amber"></div>
                  <div className="cockpit-light cockpit-light--green"></div>
                  <span className="cockpit-name">ORANE-OP-VIEW // GLOBAL COMMAND CENTER</span>
                </div>
                <div className="cockpit-bar-right">
                  <span className="cockpit-chip">ENVIRONMENT: PRODUCTION</span>
                  <span className="cockpit-chip cockpit-chip--ok">HEALTH: OPTIMAL</span>
                </div>
              </div>

              <div className="stat-grid">
                {[
                  { label: 'Incidents', dot: 'emerald', value: '12', unit: 'Active', note: '0 Critical P1s', noteExtra: '| 100% on target', noteTone: 'emerald' },
                  { label: 'Service Requests', dot: 'blue', value: '99.4%', note: 'SLA Compliance Rate', noteTone: 'sky' },
                  { label: 'Enhancements', dot: 'indigo', value: '8', unit: 'In Sprint', note: 'Release next Tuesday', noteTone: 'indigo' },
                  { label: 'Problems', dot: 'amber', value: '4', unit: 'Under RC', note: 'Root cause diagnosis', noteTone: 'amber' },
                  { label: 'Releases', dot: 'emerald', value: 'v4.2.0', note: 'Scheduled & Certified', noteTone: 'emerald' },
                  { label: 'SLA & Availability', dot: 'emerald', value: '99.99%', valueTone: 'emerald', note: 'Rolling 90-day uptime', noteTone: 'slate' },
                  { label: 'Security & Control', dot: 'emerald', value: '100%', note: 'Audit & SOX Compliant', noteTone: 'emerald' },
                  { label: 'Business Impact', dot: 'emerald', value: '$0.00', note: 'Zero revenue loss', noteTone: 'emerald' },
                ].map((stat) => (
                  <div className="stat-card" key={stat.label}>
                    <div className="stat-card-head">
                      <span>{stat.label}</span>
                      <span className={`stat-dot stat-dot--${stat.dot}`}></span>
                    </div>
                    <div className={`stat-value ${stat.valueTone ? `stat-value--${stat.valueTone}` : ''}`}>
                      {stat.value}
                      {stat.unit && <span className="stat-unit"> {stat.unit}</span>}
                    </div>
                    <div className={`stat-note stat-note--${stat.noteTone}`}>
                      <span>{stat.note}</span>
                      {stat.noteExtra && <span className="stat-note-extra">{stat.noteExtra}</span>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="cockpit-foot">
                <div className="cockpit-foot-left">
                  <span className="cockpit-foot-title">LIVE INTEGRATION SUITE THROUGHPUT:</span>
                  <span className="cockpit-foot-value">12,492 msgs/sec</span>
                </div>
                <div className="cockpit-foot-right">
                  <span className="queue-track">
                    <span className="queue-fill"></span>
                  </span>
                  <span className="queue-label">Queue Load: 24% Normal</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== LEARN FROM EVERY ISSUE ====================== */}
        <section className="op-section op-section--slate" data-purpose="learn-from-every-issue">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Knowledge Engine</div>
              <h2 className="sec-title">Learn From Every Issue</h2>
              <p className="sec-sub">Every issue makes your enterprise smarter.</p>
            </div>

            <div className="learn-grid">
              <div className="pipeline">
                <h3 className="pipeline-head">
                  <span>Incident-to-Knowledge Pipeline</span>
                  <span className="pipeline-count">7 Stages</span>
                </h3>
                {[
                  { n: 1, tone: 'rose', label: 'Issue Logged & Categorized', stage: 'Triage' },
                  { n: 2, tone: 'amber', label: 'Root Cause Analysis (5-Whys)', stage: 'Diagnosis' },
                  { n: 3, tone: 'blue', label: 'Immediate Precision Resolution', stage: 'Restoration' },
                  { n: 4, tone: 'indigo', label: 'Knowledge Base Documentation', stage: 'Graph Ingest' },
                  { n: 5, tone: 'purple', label: 'Reusable Fix Package Built', stage: 'SOP Engine' },
                  { n: 6, tone: 'cyan', label: 'RPA / Script Automation', stage: 'Self-Heal' },
                ].map((row) => (
                  <div className="pipeline-row" key={row.n}>
                    <div className={`pipeline-num tone-${row.tone}`}>{row.n}</div>
                    <div className="pipeline-label">{row.label}</div>
                    <span className="pipeline-stage">{row.stage}</span>
                  </div>
                ))}
                <div className="pipeline-row pipeline-row--final">
                  <div className="pipeline-num pipeline-num--final">7</div>
                  <div className="pipeline-label">Permanent Recurrence Prevention</div>
                  <span className="pipeline-stage">Protected</span>
                </div>
              </div>

              {/* Quote card — image */}
              <div className="learn-image">
                <img
                  src="/assets/images/knowledgehero.png"
                  alt="From recurring issues to a continuously improving enterprise"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ==================== INTELLIGENCE FOR OPERATIONS =================== */}
        <section className="op-section op-section--white" data-purpose="intelligence-for-operations">
          <div className="op-container">
            <div className="intel-grid">
              <div className="intel-left">
                <div>
                  <div className="sec-badge">Predictive AI &amp; Automation</div>
                  <h2 className="sec-title">Intelligence for Operations</h2>
                  <p className="sec-sub sec-sub--medium">Detect issues. Predict risks. Drive proactive improvement.</p>
                </div>

                <div className="intel-list">
                  {[
                    {
                      title: 'Detect recurring incidents and patterns',
                      desc: 'Automated telemetry scanning across logs, queues, and integration interfaces to identify structural anomalies.',
                      icon: (
                        <path
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      ),
                    },
                    {
                      title: 'Predict risks and potential failures',
                      desc: 'Early warning threshold indicators signal database memory exhaustion or interface congestion hours before failure.',
                      icon: (
                        <path
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      ),
                    },
                    {
                      title: 'Recommend verified resolutions',
                      desc: 'Pre-populated, peer-reviewed standard operating guidelines delivered immediately to engineers.',
                      icon: (
                        <path
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      ),
                    },
                    {
                      title: 'Surface business-critical exceptions',
                      desc: 'Distinguishes low-impact background tickets from revenue-threatening customer order blockers.',
                      icon: (
                        <>
                          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                          <path
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                          ></path>
                        </>
                      ),
                    },
                    {
                      title: 'Automate repetitive work',
                      desc: 'RPA and event-triggered scripts clear cache, reset locked background jobs, and balance queue workloads.',
                      icon: (
                        <path
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      ),
                    },
                    {
                      title: 'Enable faster, data-driven decisions',
                      desc: 'Executive dashboards link technical health directly to customer service scores and revenue velocity.',
                      icon: (
                        <path
                          d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                        ></path>
                      ),
                    },
                  ].map((row) => (
                    <div className="intel-item" key={row.title}>
                      <div className="intel-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {row.icon}
                        </svg>
                      </div>
                      <div>
                        <h4 className="intel-item-title">{row.title}</h4>
                        <p className="intel-item-desc">{row.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ==================== BUSINESS CONTEXT MATTERS ===================== */}
        <section className="op-section op-section--slate" data-purpose="business-context-matters">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Impact-Driven Triage</div>
              <h2 className="sec-title">Business Context Matters</h2>
              <p className="sec-sub">Not all issues are equal. We prioritise based on business impact.</p>
            </div>

            <div className="risk-card">
              <div className="risk-scroll">
                <table className="risk-table">
                  <thead>
                    <tr>
                      <th>Technical Symptom</th>
                      <th>Direct Business Impact</th>
                      <th>Executive Risk Classification</th>
                      <th>Orane Prioritized Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        tone: 'rose',
                        symptom: 'Failed Interface',
                        impact: 'May impact order fulfilment',
                        risk: 'Revenue at Immediate Risk ($350k/hr)',
                        response: 'Priority 1 Instant Bridge & Automated Pipeline Fallback',
                      },
                      {
                        tone: 'amber',
                        symptom: 'Stuck Invoice',
                        impact: 'Impacts cash flow & DSO',
                        risk: 'Working Capital Disruption',
                        response: 'Finance Ops accelerated routing with automated clearing rules',
                      },
                      {
                        tone: 'amber',
                        symptom: 'Pricing Error',
                        impact: 'Impacts revenue and margin leakage',
                        risk: 'Commercial Integrity Risk',
                        response: 'Pricing condition table lock check & master data synchronization',
                      },
                      {
                        tone: 'purple',
                        symptom: 'Payroll Issue',
                        impact: 'Impacts employee trust & compliance',
                        risk: 'Workforce Governance Exposure',
                        response: 'Zero-delay payroll fast-track with executive escalation',
                      },
                    ].map((row) => (
                      <tr className={`risk-row risk-row--${row.tone}`} key={row.symptom}>
                        <td className="risk-symptom">
                          <span className="risk-dot"></span>
                          {row.symptom}
                        </td>
                        <td className="risk-impact">{row.impact}</td>
                        <td className="risk-class">
                          <span className="risk-badge">{row.risk}</span>
                        </td>
                        <td className="risk-response">{row.response}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="risk-foot">
                <span className="risk-foot-dot"></span>
                <p className="risk-foot-text">
                  <span className="risk-foot-strong">We bring business context, not just technical resolution. </span>
                  Every incident is automatically cross-referenced with your balance sheet impact, customer
                  satisfaction risk, and operational continuity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =================== OPERATE ACROSS THE ENTERPRISE ================= */}
        <section className="op-section op-section--white" data-purpose="operate-across-the-enterprise">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Complete Ecosystem</div>
              <h2 className="sec-title">Operate Across the Enterprise</h2>
              <p className="sec-sub">One partner. Your complete enterprise landscape.</p>
            </div>

            <div className="eight-grid">
              {[
                { code: 'SAP', title: 'SAP S/4HANA', desc: 'ECC, S/4HANA Public & Private Cloud', href: '/enterprise-transformation/sap-s4hana' },
                { code: 'SF', title: 'SuccessFactors', desc: 'Core HR, Payroll, Talent, Learning', href: '/enterprise-transformation/successfactors' },
                { code: 'BTP', title: 'SAP BTP', desc: 'Integration Suite, Build Apps, Extension', href: '/enterprise-transformation/sap-btp-integration' },
                { code: 'API', title: 'Integrations', desc: 'Kafka, Azure Service Bus, MuleSoft', href: '/enterprise-transformation/sap-btp-integration' },
                { code: 'APP', title: 'Enterprise Apps', desc: 'Custom React, Node, MES, Portals', href: '/enterprise-transformation/digital-engineering' },
                { code: 'DAT', title: 'Data & Analytics', desc: 'SAP SAC, Snowflake, Databricks, PowerBI', href: '/datasap/analytics' },
                { code: 'CLD', title: 'Cloud Infrastructure', desc: 'AWS, Microsoft Azure, Google Cloud', href: '/enterprise-transformation/cloud' },
                { code: 'SEC', title: 'Security & Controls', desc: 'Identity Access, SOX, Cyber Auditing', href: '/Operate/cyber' },
              ].map((item) => (
                <Link className="eco-card" key={item.code} href={item.href}>
                  <div className="eco-code">{item.code}</div>
                  <h3 className="eco-title">{item.title}</h3>
                  <p className="eco-desc">{item.desc}</p>
                  <span className="eco-tag">Continuous Support</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ======================= MEASURE WHAT MATTERS ====================== */}
        <section className="op-section op-section--slate-plain" data-purpose="measure-what-matters">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Outcome Governance</div>
              <h2 className="sec-title">Measure What Matters</h2>
              <p className="sec-sub">More than SLA. Real business outcomes.</p>
            </div>

            <div className="eight-grid">
              {[
                { label: 'System Health', value: '99.99%', tone: 'navy', desc: 'Core ERP and database uptime guaranteed.' },
                { label: 'Service Quality', value: '98.7%', tone: 'blue', desc: 'First-contact resolution rate on standard issues.' },
                { label: 'Business Continuity', value: '0', tone: 'green', desc: 'Zero unplanned P1 outages across 12 months.' },
                { label: 'User Experience', value: '4.9/5', tone: 'navy', desc: 'Verified enterprise user satisfaction score.' },
                { label: 'Automation', value: '42%', tone: 'blue', desc: 'Routine operational tasks shifted to bots.' },
                { label: 'Improvement', value: '350+', tone: 'navy', desc: 'Value-driving enhancements shipped annually.' },
                { label: 'Cost Efficiency', value: '-28%', tone: 'green', desc: 'Reduction in recurring operational TCO.' },
                { label: 'Value Realised', value: '$4.2M', tone: 'blue', desc: 'Direct documented savings for top client.' },
              ].map((stat) => (
                <div className="metric-card" key={stat.label}>
                  <p className="metric-label">{stat.label}</p>
                  <p className={`metric-value metric-value--${stat.tone}`}>{stat.value}</p>
                  <p className="metric-desc">{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TRANSFORMATION TO CONTINUOUS VALUE ============== */}
        <section className="op-section op-section--white" data-purpose="transformation-to-continuous-value">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">The Journey Roadmap</div>
              <h2 className="sec-title">From Transformation to Continuous Value</h2>
              <p className="sec-sub">
                Your go-live is not the finish line. It&apos;s the start of a better, smarter, more valuable
                enterprise.
              </p>
            </div>

            <div className="journey-scroll">
              <div className="journey-track">
                {[
                  { phase: 'PHASE 1', title: 'Transform', sub: 'Design & Build', style: 'default', edge: 'first' },
                  { phase: 'PHASE 2', title: 'Go Live', sub: 'Deploy & Cutover', style: 'default' },
                  { phase: 'PHASE 3', title: 'Stabilise', sub: 'Hypercare Ops', style: 'default' },
                  { phase: 'PHASE 4', title: 'Operate', sub: 'Run & Support', style: 'blue-soft' },
                  { phase: 'PHASE 5', title: 'Learn', sub: 'Capture Knowledge', style: 'blue-soft' },
                  { phase: 'PHASE 6', title: 'Improve', sub: 'Optimise & Enhance', style: 'blue-mid' },
                  { phase: 'PHASE 7', title: 'Automate', sub: 'Eliminate Repeat Work', style: 'navy' },
                  { phase: 'PHASE 8', title: 'Optimise', sub: 'Drive More Value', style: 'blue', edge: 'last' },
                ].map((p) => (
                  <div
                    className={`phase phase--${p.style} ${p.edge ? `phase--${p.edge}` : ''}`}
                    key={p.phase}
                  >
                    <div>
                      <span className="phase-label">{p.phase}</span>
                      <h4 className="phase-title">{p.title}</h4>
                    </div>
                    <div className="phase-sub">{p.sub}</div>
                  </div>
                ))}
              </div>
              <div className="journey-caption">
                <span className="journey-caption-chip">A CONTINUOUS VALUE JOURNEY</span>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="final-cta" data-purpose="final-call-to-action">
          <div className="op-container">
            <div className="final-cta-box">
              <div className="final-cta-left">
                <span className="final-cta-eyebrow">OPERATIONAL EXCELLENCE GUARANTEED</span>
                <h2 className="final-cta-title">Your Go-Live Is Not the Finish Line.</h2>
                <p className="final-cta-text">
                  Operate reliably. Learn continuously. Improve relentlessly. Partner with Orane today.
                </p>
              </div>
              <div className="final-cta-right">
                <div className="final-cta-tagline">
                  <span className="final-cta-tagline-top">STABLE TODAY.</span>
                  <span className="final-cta-tagline-bottom">STRONGER TOMORROW.</span>
                </div>
                <Link className="btn-primary btn-primary--cta" href="/tellusyourproblem#tell-details">
                  Talk to Orane
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= WHY 52 PROBLEMS =========================== */}
        <section className="why52" data-purpose="why-52-problems">
          <div className="op-container">
            <div className="why52-head">
              <span className="why52-eyebrow">WHY 52 PROBLEMS?</span>
              <h2 className="why52-title">Real Problems. Real Impact. A Clearer Conversation.</h2>
              <p className="why52-text">
                We organised the most common and high-impact business problems into 52 distinct,
                real-world problems — so you can quickly find the conversations that matter to you.
              </p>
            </div>

            <div className="why52-grid">
              {[
                {
                  n: '01',
                  title: 'Across the Enterprise',
                  desc: 'The 52 problems cover the full enterprise — from supply chain and finance to customers, people, assets and the office of the CEO.',
                },
                {
                  n: '02',
                  title: 'Based on Real Experience',
                  desc: 'They are drawn from our experience across industries, clients and transformations — not a theoretical framework.',
                },
                {
                  n: '03',
                  title: 'From Question to Value',
                  desc: 'Each problem page connects your question to business context, relevant solutions, customer examples and a path to measurable value.',
                },
              ].map((item) => (
                <div className="why52-card" key={item.n}>
                  <div className="why52-num">{item.n}</div>
                  <h3 className="why52-card-title">{item.title}</h3>
                  <p className="why52-card-desc">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="why52-bar">
              <p className="why52-quote">
                &quot;Start with a problem. Build the right solution. Create real value.&quot;{' '}
                <span className="why52-quote-by">— The Orane Approach</span>
              </p>
              <Link className="btn-small" href="/52problem">
                Explore All 52 Problems
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}